import { Link } from "react-router-dom";
import { ShieldCheck, Sun, MapPin, GraduationCap, Phone, Mail, ArrowRight } from "lucide-react";
import { Seo, PHONE_TEL, EMAIL } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";

const ROLES = [
  { icon: ShieldCheck, h: "Uniformed Security Guards", p: "Static guarding for commercial, residential, healthcare and industrial sites across Ontario — days, nights, weekends." },
  { icon: MapPin, h: "Mobile Patrol Officers", p: "Marked vehicle patrols, lock checks and alarm response across assigned territories — a valid driver's licence required." },
  { icon: Sun, h: "Community Security Officers", p: "Culturally grounded security roles serving First Nations communities, band facilities and community events." },
  { icon: GraduationCap, h: "Event & Fire Watch Guards", p: "Festival, concert and crowd management shifts, plus Ontario Fire Code compliant fire watch assignments." },
];

const HIRING_REGIONS = [
  "Thunder Bay", "Kenora", "Dryden", "Sioux Lookout", "Fort Frances", "Red Lake",
  "Greater Sudbury", "Sault Ste. Marie", "North Bay", "Timmins", "Toronto & the GTA", "Ottawa",
];

export default function Careers() {
  return (
    <div data-testid="careers-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title="Security Guard Careers Ontario | 1st Nations Security Jobs"
        description="Security guard jobs across Ontario with a 100% Indigenous-owned company. Hiring in Thunder Bay, Kenora, Sioux Lookout & beyond. Apply: admin@1stnation-security.com."
        path="/careers"
      />
      <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3">Careers — Join Our Team</p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-6">
        Security Guard Careers in Ontario
      </h1>
      <div className="max-w-3xl space-y-6">
        <p className="text-base text-slate-300 leading-relaxed">
          1st Nations Security is a 100% Indigenous-owned security guard company built in Northwestern Ontario — and
          we are growing. We hire licensed security guards, mobile patrol officers, event security staff and fire
          watch guards across Ontario, with priority hiring and training support for First Nations community members.
        </p>
        <p className="text-base text-slate-300 leading-relaxed">
          When you work with us, you protect your own community. Our guards serve hospitals, construction sites,
          mines, retail stores, powwows and public events — professionally trained, fairly scheduled and supported by
          a 24/7 dispatch team that always has your back.
        </p>
      </div>

      <section data-testid="careers-roles-section" className="my-14">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">Positions We Hire For</h2>
        <div className="grid sm:grid-cols-2 gap-5 max-w-5xl">
          {ROLES.map((r) => (
            <div key={r.h} data-testid={`careers-role-${r.h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
              <r.icon className="h-8 w-8 text-amber-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{r.h}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{r.p}</p>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="careers-requirements-section" className="rounded-2xl border border-amber-500/20 bg-slate-900/60 p-8 max-w-3xl my-14">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">What You Need</h2>
        <ul className="space-y-3">
          {[
            "A valid Ontario security guard licence (PSISA) — or the motivation to get one; we help community members through licensing and training",
            "First aid and CPR certification (or willingness to obtain it)",
            "Reliability, professionalism and strong communication skills",
            "For patrol roles: a valid Ontario driver's licence",
            "For northern and remote postings: readiness for rotational work",
          ].map((req) => (
            <li key={req} className="flex items-start gap-3 text-sm text-slate-300">
              <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" /> {req}
            </li>
          ))}
        </ul>
      </section>

      <section data-testid="careers-regions-section" className="my-14">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          Now Hiring Across Northwestern Ontario and Beyond
        </h2>
        <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">
          Our strongest hiring needs are in Northwestern Ontario — Thunder Bay, Kenora, Dryden, Sioux Lookout, Fort
          Frances and Red Lake — with opportunities across Northern Ontario, the GTA and Ottawa as we grow.
        </p>
        <div className="flex flex-wrap gap-3">
          {HIRING_REGIONS.map((r) => (
            <span key={r} data-testid={`careers-region-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/70 px-4 py-2 text-sm text-slate-300">
              <MapPin className="h-3.5 w-3.5 text-amber-500" /> {r}
            </span>
          ))}
        </div>
      </section>

      <section data-testid="careers-apply-section" className="my-14 max-w-3xl">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">How to Apply</h2>
        <p className="text-base text-slate-300 leading-relaxed mb-6">
          Email your resumé and the region you want to work in to{" "}
          <a href={`mailto:${EMAIL}?subject=Security%20Guard%20Application`} data-testid="careers-apply-email-link" className="text-amber-400 hover:text-amber-300 font-semibold">
            {EMAIL}
          </a>{" "}
          with the subject line "Security Guard Application", or call our team at{" "}
          <a href={PHONE_TEL} data-testid="careers-apply-phone-link" className="text-amber-400 hover:text-amber-300 font-semibold">
            +1 807-770-0243
          </a>
          . No online forms — a real person reviews every application.
        </p>
        <div className="flex flex-wrap gap-4">
          <a href={`mailto:${EMAIL}?subject=Security%20Guard%20Application`} data-testid="careers-apply-email-button" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 transition-all duration-200 hover:bg-amber-400 hover:scale-[1.02]">
            <Mail className="h-4 w-4" /> Email Your Resumé
          </a>
          <a href={PHONE_TEL} data-testid="careers-apply-call-button" className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-6 py-3 text-sm font-semibold text-amber-300 transition-colors duration-200 hover:bg-amber-500/10">
            <Phone className="h-4 w-4" /> Call +1 807-770-0243
          </a>
        </div>
      </section>

      <p className="text-sm text-slate-400 max-w-3xl">
        Looking to hire guards instead of becoming one?{" "}
        <Link to="/security-services" data-testid="careers-services-link" className="text-amber-400 hover:text-amber-300 font-semibold">
          Browse our Ontario security services <ArrowRight className="inline h-3.5 w-3.5" />
        </Link>
      </p>

      <CtaBand
        heading="Protect Your Community. Build Your Career."
        sub="Join a First Nations security company that invests in its people. Email admin@1stnation-security.com or call +1 807-770-0243 to apply today."
      />
    </div>
  );
}
